import React from "react";
import { useDataGridUrlState } from "./dataGridUrlState/useDataGridUrlState.js";
import { useDataGridPresets } from "./dataGridPresets/useDataGridPresets.js";

/**
 * Combines URL persistence and optional preset management for AsyncDataGrid.
 *
 * @param {Object} options
 * @param {string} [options.urlStateKey]
 * @param {boolean} [options.hidePresets]
 * @param {number} options.defaultPageSize
 * @param {Object} [options.initialState]
 * @param {string[]} [options.columnFields]
 * @param {Function} [options.onFilterModelChange]
 * @param {Function} [options.onSortModelChange]
 * @param {Function} [options.onPaginationModelChange]
 * @param {Function} [options.onColumnVisibilityModelChange]
 */
export function useAsyncDataGridState({
  urlStateKey,
  hidePresets = false,
  defaultPageSize,
  initialState,
  columnFields,
  onFilterModelChange,
  onSortModelChange,
  onPaginationModelChange,
  onColumnVisibilityModelChange,
}) {
  const presetsEnabled = Boolean(urlStateKey && !hidePresets);
  const presetsHook = useDataGridPresets(urlStateKey, presetsEnabled);

  const urlStateBindings = useDataGridUrlState({
    urlStateKey,
    defaultPageSize,
    initialState,
    columnFields,
    onFilterModelChange,
    onSortModelChange,
    onPaginationModelChange,
    onColumnVisibilityModelChange,
    presetsBinding: presetsEnabled
      ? {
          enabled: true,
          loaded: presetsHook.loaded,
          presets: presetsHook.presets,
        }
      : null,
  });

  const { createPreset: createPresetApi, ...presetsHookRest } = presetsHook;
  const { selectPreset, ...urlStateRest } = urlStateBindings;

  const createPreset = React.useCallback(
    async (dtoIn) => {
      const created = await createPresetApi(dtoIn);
      if (created?._id) {
        selectPreset(created);
      }
      return created;
    },
    [createPresetApi, selectPreset],
  );

  const presetsBindings = presetsEnabled
    ? {
        gridKey: urlStateKey,
        presets: presetsHookRest.presets,
        loaded: presetsHookRest.loaded,
        activePresetId: urlStateRest.activePresetId,
        selectPreset,
        clearActivePreset: urlStateRest.clearActivePreset,
        getCurrentPresetState: urlStateRest.getCurrentPresetState,
        createPreset,
        renamePreset: presetsHookRest.renamePreset,
        setDefaultPreset: presetsHookRest.setDefaultPreset,
        deletePreset: presetsHookRest.deletePreset,
      }
    : null;

  return {
    urlStateBindings,
    presetsBindings,
  };
}
